/**
 * Consumo das consultas ao bureau e precos da plataforma (C-VGD).
 *
 * As chamadas ficam neste modulo, e nao espalhadas pelas telas. O relatorio da
 * empresa exige `credito.consumo.view`; o da plataforma e os precos sao da
 * administracao da plataforma, que o servidor confere a cada requisicao. Ver
 * docs/CREDITO.md, secao 8, e docs/API.md, secao 7.51.
 */

import { api } from '@/shared/api/client';
import type { Money } from '@/shared/api/types';

export type BureauProduct =
  | 'analise_credito_cpf'
  | 'analise_credito_cnpj'
  | 'analise_credito_master_cnpj'
  | 'analise_credito_master_cpf';

export type CallResult = 'concluida' | 'parcial' | 'falhou' | 'incerta';
export type CallKind = 'consulta' | 'autenticacao';
export type CallBillable = 'sim' | 'nao' | 'desconhecido';

export interface ConsumptionFilters {
  from?: string;
  to?: string;
  product?: BureauProduct | '';
  result?: CallResult | '';
  kind?: CallKind | '';
  requested_by?: string;
  policyholder_id?: string;
  tenant_id?: string;
  limit?: number;
  offset?: number;
}

export interface ConsumptionLine {
  id: string;
  tenant_id?: string;
  tenant_name?: string;
  consultation_id: string;
  policyholder_id: string;
  policyholder_label: string;
  requested_by: string | null;
  requested_label: string;
  provider: 'ficticio' | 'vigidata';
  simulated: boolean;
  environment: 'teste' | 'producao' | 'simulado';
  kind: CallKind;
  endpoint: string;
  product: BureauProduct | '';
  stage: 'basica' | 'master';
  request_id: string;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  http_status: number | null;
  result: CallResult;
  billable: CallBillable;
  failure_category: string;
  price_id: string | null;
  /** Nulo e custo desconhecido: nenhum preco cadastrado no instante da chamada. */
  cost: Money | null;
}

export interface ConsumptionTotals {
  calls: number;
  consultations: number;
  authentications: number;
  known_cost: Money;
  unknown_cost_calls: number;
  billable_unknown_calls: number;
  by_result: Record<string, number>;
}

export interface ConsumptionReport {
  from: string;
  to: string;
  totals: ConsumptionTotals;
  items: ConsumptionLine[];
  total: number;
  limit: number;
  offset: number;
}

export interface BureauPrice {
  id: string;
  provider: 'vigidata';
  product: BureauProduct;
  price: Money;
  effective_from: string;
  notes: string;
  created_label: string;
  created_at: string;
  revoked_at: string | null;
  revoked_label: string;
  revoked_reason: string;
  active_now: boolean;
  scheduled: boolean;
}

export interface BureauPriceInput {
  product: BureauProduct;
  price: Money;
  effective_from?: string;
  notes: string;
}

export type LimitScope = 'empresa' | 'corretor' | 'tomador';
export type LimitPeriod = 'mensal' | 'diario';
export type LimitSubjectKind = 'corretor' | 'tomador';

/** Um limite de consulta com o uso do periodo corrente (ADR 0098). */
export interface ConsultationLimit {
  scope: LimitScope;
  subject_id: string;
  subject_label: string;
  subject_document: string;
  /** So na empresa: de onde vem o limite que vale. */
  source?: 'parametro' | 'plataforma';
  /** "Sem limite", escolha expressa. `limit` e `remaining` ficam nulos. */
  unlimited: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  reached: boolean;
  period: LimitPeriod;
  period_from: string;
  period_to: string;
  /** Falso no corretor com o bloqueio desligado: o limite so informa. */
  enforced: boolean;
  auto_block: boolean;
  unblocked_in_period: boolean;
  reason: string;
  updated_label: string;
  updated_at: string | null;
}

/**
 * Limite padrao por corretor (ADR 0102): vale para a corretora sem limite proprio, nas
 * consultas pedidas por ela.
 */
export interface BrokerDefaultLimit {
  limit: number;
  /** `padrao`: 10 por mes, a decisao do dono; `plataforma`: definido para a empresa. */
  source: 'padrao' | 'plataforma';
  reason: string;
  updated_label: string;
  updated_at: string | null;
}

/** Custo estimado do mes, em centavos. So a administracao da plataforma recebe. */
export interface ConsultationLimitsCost {
  month_spent: Money;
  /** Maior preco vigente entre os produtos. Nulo: nenhum preco cadastrado. */
  unit_price: Money | null;
  /** Gasto mais o saldo pelo maior preco. Nulo sem limite ou sem preco. */
  estimated_max: Money | null;
}

export interface ConsultationLimits {
  tenant_id: string;
  tenant_name?: string;
  company: ConsultationLimit;
  broker_default: BrokerDefaultLimit;
  items: ConsultationLimit[];
  cost?: ConsultationLimitsCost;
}

export interface LimitSubject {
  id: string;
  label: string;
  /** O CPF vem mascarado. */
  document: string;
}

export type CompanyLimitInput =
  { max_queries: number; reason: string } | { unlimited: true; reason: string };

export interface BrokerLimitInput {
  max_queries: number;
  period: LimitPeriod;
  auto_block: boolean;
  reason: string;
}

export interface PolicyholderLimitInput {
  max_queries: number;
  reason: string;
}

function rotaDosLimites(tenantId: string, resto = ''): string {
  return `/api/v1/platform/tenants/${encodeURIComponent(tenantId)}/consultation-limits${resto}`;
}

/** Monta a consulta da URL sem os filtros vazios. */
export function consultaDosFiltros(filtros: ConsumptionFilters): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === '' || valor === null) continue;
    busca.set(chave, String(valor));
  }
  const texto = busca.toString();
  return texto ? `?${texto}` : '';
}

/** A consulta como a segunda etapa a devolve (o que a tela usa dela). */
export interface StageConsultation {
  id: string;
  stage: 'basica' | 'master';
  product: BureauProduct | '';
  status: CallResult;
  simulated: boolean;
  reused: boolean;
  /** A Master foi reaproveitada e o motor reavaliou as regras, sem custo. */
  reanalyzed?: boolean;
}

export const creditConsumption = {
  requestMaster: (policyholderId: string, confirmCost: boolean) =>
    api.post<StageConsultation>(
      `/api/v1/policyholders/${encodeURIComponent(policyholderId)}/credit-consultations/master`,
      { confirm_cost: confirmCost },
    ),
  tenantReport: (filtros: ConsumptionFilters, signal?: AbortSignal) =>
    api.get<ConsumptionReport>(
      `/api/v1/credit-consumption${consultaDosFiltros(filtros)}`,
      signal,
    ),
  platformReport: (filtros: ConsumptionFilters, signal?: AbortSignal) =>
    api.get<ConsumptionReport>(
      `/api/v1/platform/credit-consumption${consultaDosFiltros(filtros)}`,
      signal,
    ),
  listPrices: (signal?: AbortSignal) =>
    api.get<BureauPrice[]>('/api/v1/platform/credit-bureau-prices', signal),
  createPrice: (input: BureauPriceInput) =>
    api.post<BureauPrice>('/api/v1/platform/credit-bureau-prices', input),
  revokePrice: (priceId: string, reason: string) =>
    api.post<BureauPrice>(
      `/api/v1/platform/credit-bureau-prices/${encodeURIComponent(priceId)}/revocation`,
      { reason },
    ),

  // Limites de consulta (ADR 0098). A empresa le; a plataforma edita.
  tenantLimits: (signal?: AbortSignal) =>
    api.get<ConsultationLimits>('/api/v1/credit-consultation-limits', signal),
  platformLimits: (tenantId: string, signal?: AbortSignal) =>
    api.get<ConsultationLimits>(rotaDosLimites(tenantId), signal),
  searchLimitSubjects: (
    tenantId: string,
    kind: LimitSubjectKind,
    termo: string,
    signal?: AbortSignal,
  ) =>
    api.get<LimitSubject[]>(
      rotaDosLimites(tenantId, `/subjects${consultaDeBusca(kind, termo)}`),
      signal,
    ),
  setCompanyLimit: (tenantId: string, input: CompanyLimitInput) =>
    api.put<ConsultationLimits>(rotaDosLimites(tenantId, '/company'), input),
  clearCompanyLimit: (tenantId: string, reason: string) =>
    api.post<ConsultationLimits>(rotaDosLimites(tenantId, '/company/removal'), {
      reason,
    }),
  setBrokerDefaultLimit: (
    tenantId: string,
    input: { max_queries: number; reason: string },
  ) => api.put<ConsultationLimits>(rotaDosLimites(tenantId, '/broker-default'), input),
  clearBrokerDefaultLimit: (tenantId: string, reason: string) =>
    api.post<ConsultationLimits>(rotaDosLimites(tenantId, '/broker-default/removal'), {
      reason,
    }),
  setBrokerLimit: (tenantId: string, brokerId: string, input: BrokerLimitInput) =>
    api.put<ConsultationLimits>(
      rotaDosLimites(tenantId, `/brokers/${encodeURIComponent(brokerId)}`),
      input,
    ),
  removeBrokerLimit: (tenantId: string, brokerId: string, reason: string) =>
    api.post<ConsultationLimits>(
      rotaDosLimites(tenantId, `/brokers/${encodeURIComponent(brokerId)}/removal`),
      { reason },
    ),
  setPolicyholderLimit: (
    tenantId: string,
    policyholderId: string,
    input: PolicyholderLimitInput,
  ) =>
    api.put<ConsultationLimits>(
      rotaDosLimites(tenantId, `/policyholders/${encodeURIComponent(policyholderId)}`),
      input,
    ),
  removePolicyholderLimit: (tenantId: string, policyholderId: string, reason: string) =>
    api.post<ConsultationLimits>(
      rotaDosLimites(
        tenantId,
        `/policyholders/${encodeURIComponent(policyholderId)}/removal`,
      ),
      { reason },
    ),
};

function consultaDeBusca(kind: LimitSubjectKind, termo: string): string {
  return `?${new URLSearchParams({ kind, q: termo }).toString()}`;
}
